'use client';
import { useState } from 'react';
import Slider from 'react-slick';
import Image from 'next/image';

import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { ExperienceMedia } from '@/services/experienceTypes';
import getMediaFilterUrl from '@/utils/get-media-filter';
import { cn } from '@/lib/utils';
import Spinner from '@/components/Spinner';

import VideoModal from './VideoModal';

type Props = {
  handleClose: () => void;
  medias: ExperienceMedia[];
  hasVideo: boolean;
  videoUrl: string | null;
};

export default function GalleryModalCarousel({
  handleClose,
  medias,
  hasVideo,
  videoUrl,
}: Props) {
  const [sliderRef, setSliderRef] = useState<Slider | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [loadedSlides, setLoadedSlides] = useState<Set<number>>(new Set());

  const markSlideLoaded = (index: number) =>
    setLoadedSlides((prev) => new Set(prev).add(index));

  const settings = {
    dots: false,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    beforeChange: (current: number, next: number) => {
      setActiveSlide(next);
    },
  };

  const images = medias.map((media, index) => {
    const filterUrl = getMediaFilterUrl({
      mediaFilters: media.mediaFilters,
      filterName: 'large_gallery_wm',
    });

    const smallImageSource = getMediaFilterUrl({
      mediaFilters: media.mediaFilters,
      filterName: ['small_1-1', 'small_3-2', '480w_webp'],
    });

    const isLoaded = loadedSlides.has(index);

    return (
      <div key={index} className="relative h-full overflow-hidden">
        {!isLoaded && (
          <div className="absolute inset-0 z-30 flex items-center justify-center">
            <Spinner className="text-gray-850" />
          </div>
        )}
        {smallImageSource && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={smallImageSource}
            alt=""
            aria-hidden="true"
            className={cn(
              'absolute inset-0 z-20 w-full h-full object-cover scale-[1.1] transition-opacity duration-300',
              isLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100',
            )}
            style={{ filter: 'blur(12px)' }}
          />
        )}
        <Image
          src={filterUrl ? filterUrl : ''}
          alt="Experience image"
          fill
          loading="eager"
          onLoad={() => markSlideLoaded(index)}
          onError={() => markSlideLoaded(index)}
          className={cn(
            'object-cover h-full mx-auto !w-auto transition-opacity duration-500',
            isLoaded ? 'opacity-100' : 'opacity-0',
          )}
          sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
        />
      </div>
    );
  });

  return (
    <div className="block max-h-[100vh] top-[0px] left-[0px] right-0 bottom-0 margin-0 fixed w-full h-full min-h-full bg-white z-[999] p-4">
      <button
        onClick={() => handleClose()}
        className="absolute top-[1rem] right-[1rem] mb-0 leading-[1] z-10 cursor-pointer appearance-none text-black">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-10 h-10 fill-current text-gray-500"
          viewBox="0 -960 960 960">
          <path d="m256-168-88-88 224-224-224-224 88-88 224 224 224-224 88 88-224 224 224 224-88 88-224-224-224 224Z" />
        </svg>
      </button>
      <button
        onClick={sliderRef?.slickPrev}
        className="hidden lg:block absolute top-[50%] cursor-pointer left-0 translate-y-[-50%] outline-none z-10">
        <FiChevronLeft className="w-20 h-20 text-gray-500" />
      </button>
      <div className="h-full flex items-center justify-center flex-col">
        <Slider
          ref={setSliderRef}
          className="h-[50%] mt-[60px] lg:mt-0 w-full lg:w-[90%] lg:h-[calc(100%-90px)] z-10 [&>.slick-list]:h-full [&>.slick-list>.slick-track]:h-full [&>.slick-list>.slick-track>.slick-slide>div]:w-full [&>.slick-list>.slick-track>.slick-slide>div]:h-full"
          {...settings}>
          {images}
        </Slider>
        <div className="flex justify-between items-center w-full py-[10px] lg:w-[90%] min-h-[50px]">
          <small>{`${activeSlide + 1}/${medias.length}`}</small>
          {hasVideo && (
            <div>
              <VideoModal isText videoUrl={videoUrl} />
            </div>
          )}
        </div>
      </div>
      <button
        onClick={sliderRef?.slickNext}
        className="hidden lg:block absolute top-[50%] cursor-pointer right-0 translate-y-[-50%] outline-none z-10">
        <FiChevronRight className="w-20 h-20 text-gray-500" />
      </button>
    </div>
  );
}
